import { useEffect, useState } from 'react';
import axios from 'axios';
import { Bookmark, BookmarkCheck, Plus, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { devError } from '../utils/logger';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';

/**
 * AddToProjectButton — gold pill that opens a dropdown of the user's
 * projects, with ticks next to projects that already contain this listing.
 * Click an unchecked project to add; click a checked one to remove.
 *
 * The "+ New project" item opens a tiny inline create-form right inside the
 * dropdown — no modal, no extra round trip needed before saving.
 *
 * Anonymous users get bounced to /auth (the projects API requires login).
 */
export default function AddToProjectButton({ listingId, compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [memberOf, setMemberOf] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [{ data: list }, { data: byL }] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/projects/by-listing/${listingId}`),
      ]);
      setProjects(list.projects || []);
      setMemberOf(new Set(byL.project_ids || []));
    } catch (e) {
      devError('AddToProject load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && user) refresh();
    // We deliberately re-fetch every time the menu opens — the user could
    // have created a project on another tab. Cheap query, big UX win.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const toggle = async (project) => {
    setSavingId(project.project_id);
    try {
      if (memberOf.has(project.project_id)) {
        await axios.delete(`${API}/projects/${project.project_id}/listings/${listingId}`);
        setMemberOf((prev) => {
          const next = new Set(prev);
          next.delete(project.project_id);
          return next;
        });
        toast.success(`Removed from ${project.name}`);
      } else {
        await axios.post(`${API}/projects/${project.project_id}/listings`, { listing_id: listingId });
        setMemberOf((prev) => new Set(prev).add(project.project_id));
        toast.success(`Saved to ${project.name}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update project');
    } finally {
      setSavingId(null);
    }
  };

  const createAndAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSavingId('__new__');
    try {
      const { data: project } = await axios.post(`${API}/projects`, {
        name: newName.trim(),
        description: '',
      });
      await axios.post(`${API}/projects/${project.project_id}/listings`, { listing_id: listingId });
      toast.success(`Saved to ${project.name}`);
      setNewName('');
      setCreatingNew(false);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create project');
    } finally {
      setSavingId(null);
    }
  };

  if (!user) {
    return (
      <button
        type="button"
        onClick={() => navigate('/auth?next=' + encodeURIComponent(window.location.pathname))}
        className={`inline-flex items-center gap-1.5 rounded-md border border-border/40 hover:border-accent/50 hover:text-accent transition-colors ${compact ? 'text-xs px-2 py-1' : 'text-sm px-3 py-2'}`}
        data-testid="add-to-project-anon"
      >
        <Bookmark className="w-3.5 h-3.5" />
        Save to project
      </button>
    );
  }

  const inAny = memberOf.size > 0;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 rounded-md border transition-colors ${
            inAny
              ? 'border-accent/60 text-accent bg-accent/5'
              : 'border-border/40 hover:border-accent/50 hover:text-accent'
          } ${compact ? 'text-xs px-2 py-1' : 'text-sm px-3 py-2'}`}
          data-testid="add-to-project-trigger"
        >
          {inAny ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
          {compact ? (inAny ? `Saved (${memberOf.size})` : 'Save') : (inAny ? `Saved to ${memberOf.size}` : 'Save to project')}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 bg-card border-border"
        data-testid="add-to-project-menu"
      >
        <DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">
          Save to project
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {loading && (
          <div className="px-3 py-4 text-xs text-muted-foreground inline-flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        )}

        {!loading && projects.length === 0 && !creatingNew && (
          <div className="px-3 py-3 text-xs text-muted-foreground">
            You don't have any projects yet — create one below.
          </div>
        )}

        {!loading && projects.map((p) => {
          const isMember = memberOf.has(p.project_id);
          const isSaving = savingId === p.project_id;
          return (
            <DropdownMenuItem
              key={p.project_id}
              onSelect={(e) => { e.preventDefault(); toggle(p); }}
              className="flex items-center gap-2 cursor-pointer"
              data-testid={`add-to-project-item-${p.project_id}`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> :
                  isMember ? <Check className="w-3.5 h-3.5 text-accent" /> : null}
              </div>
              <span className="flex-1 truncate">{p.name}</span>
              <span className="text-[10px] text-muted-foreground">
                {(p.listing_ids || []).length}
              </span>
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />
        {creatingNew ? (
          <form onSubmit={createAndAdd} className="px-3 py-2 flex gap-2">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Project name"
              maxLength={80}
              className="flex-1 px-2 py-1.5 text-sm bg-background border border-border/40 rounded-md focus:outline-none focus:border-accent"
              data-testid="new-project-name"
            />
            <button
              type="submit"
              disabled={savingId === '__new__'}
              className="text-xs px-3 py-1.5 rounded-md bg-accent text-background font-medium"
              data-testid="new-project-save"
            >
              {savingId === '__new__' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </button>
          </form>
        ) : (
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); setCreatingNew(true); }}
            className="cursor-pointer text-accent"
            data-testid="add-to-project-new"
          >
            <Plus className="w-3.5 h-3.5 mr-2" />
            New project…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
